/**
 * Who the first-run panel is for, answered with the sidebar's own verdict.
 *
 * `GET /onboarding` does not consult `tenant_features` and says so out loud —
 * `onboarding.service.ts`: *"Reach is the pillar that exists; `tenant_features`
 * is not consulted here. Whether the panel should be drawn at all is
 * `navigationTree`'s and the web app's decision."* (That service header predates
 * slice 65; the pillar it means by "Reach" is the one called Connect now.) That
 * is the right split: the
 * five facts are true or false whoever asks, and duplicating a pillar verdict on
 * that route would be the second vocabulary `overviewPillar` exists to prevent.
 * It does mean the decision has to be taken **here**, and this file is where it
 * is taken. Until it was, it was taken nowhere.
 *
 * ## What went wrong with taking it nowhere
 *
 * Every step on the checklist is Connect work: the SES connection on
 * Integrations, then Audiences and Templates (the shared layers Connect sends
 * from), then Campaigns. Slice 15 made the sidebar entitlement-aware, so a
 * workspace without that pillar gets its group with no links and the line "Not
 * on this plan"; the Overview's own pillar card has carried that badge since
 * slice 10. A panel mounted above both of them, offering a primary button into
 * `/audiences/upload`, is the same screen answering "do I have Connect" twice,
 * differently — and the button is the more authoritative-looking half. Nothing
 * breaks, because entitlement is a statement rather than a lock and the routes
 * resolve; what breaks is the panel's one promise, which is that it describes
 * *this* workspace.
 *
 * A tenant in that state is constructible today and not hypothetically: the
 * admin contracts require only `.min(1)` of the three features and the tenant
 * PATCH replaces the enabled set wholesale.
 *
 * ## It asks `navPillar`, and that is the whole point
 *
 * The obvious implementation is `features.includes('connect')`. This calls
 * {@link navPillar} instead and compares its state, so the panel is drawn on
 * exactly the verdict the sidebar group is drawn on — one function, one answer,
 * and a pillar that later becomes `not_built` (Connect's sections emptied,
 * `PILLAR_IS_BUILT` flipped) takes the panel with it without anybody
 * remembering to look here. A boolean of our own would be a second opinion
 * about entitlement living one component away from the first, which is the
 * drift the shared pillar vocabulary was added to stop.
 *
 * ## Why it lives beside the panel and not inside it
 *
 * `OnboardingPanel` imports the API client, which imports `lib/config.ts`, which
 * reads `import.meta.env` and therefore only evaluates inside Vite. A predicate
 * declared in that file could not be reached by `scripts/verify-*.tsx`, which
 * run under `tsx`. Here it is a leaf over `@wend/shared` alone, so the suite
 * imports it directly and asserts it against the sidebar it renders from the
 * same features — which is the assertion that matters, the two surfaces
 * agreeing, rather than a restatement of this function's body.
 */
import { navPillar, type Feature } from '@wend/shared';

/**
 * The pillar every step on this checklist belongs to.
 *
 * `satisfies Feature`, so the day the vocabulary changes this stops compiling
 * rather than silently asking about a pillar that no longer exists. The
 * checklist is the email path end to end — connect SES, import contacts, build
 * an email template, test-send, run a campaign — and the contract's header is
 * explicit that a Meta-only workspace is a hole it leaves open rather than a
 * second pillar it serves.
 *
 * **`'connect'` since slice 65, and the steps did not change — the word did.**
 * Slice 65 split the old Reach into Reach (paid ads on the workspace's own Meta
 * ad account) and Connect (email and WhatsApp). Every step here is the second
 * half, so the panel follows Connect. Left on `'reach'`, an ads-only plan would
 * be walked through connecting SES and sending an email campaign it does not
 * have, and an email-only plan would lose its first-run checklist. Migration
 * 0048 gave every workspace with Reach the Connect row as well, so no workspace
 * that saw this panel before the split stops seeing it.
 */
export const ONBOARDING_PILLAR = 'connect' as const satisfies Feature;

/**
 * Whether this workspace should be shown the first-run panel at all.
 *
 * `undefined` — no session yet — is `false` rather than a guess. The panel is
 * mounted inside an authenticated route so the case is momentary, and the honest
 * answer to "does this workspace have Connect" before there is a workspace is not
 * "probably".
 */
export function onboardingPanelApplies(features: readonly Feature[] | undefined): boolean {
  if (features === undefined) return false;

  return navPillar(ONBOARDING_PILLAR, features.includes(ONBOARDING_PILLAR)).state === 'available';
}
